// src/hcaptcha.tsx
import { createEffect, on, onCleanup, onMount } from "solid-js";
import { createStore } from "solid-js/store";
import { hCaptchaLoader } from "@hcaptcha/loader";
var HCaptcha = (props) => {
  let apiScriptRequest = false;
  const loadCaptcha = () => {
    if (apiScriptRequest) return;
    const mountParams = {
      render: "explicit",
      apihost: props?.apihost,
      assethost: props?.assethost,
      endpoint: props?.endpoint,
      hl: props?.languageOverride,
      host: props?.host,
      imghost: props?.imghost,
      recaptchacompat: props?.reCaptchaCompat === false ? "off" : void 0,
      reportapi: props?.reportapi,
      sentry: props?.sentry,
      custom: props?.custom,
      loadAsync: props?.loadAsync ?? true,
      cleanup: props?.cleanup ?? true,
      scriptSource: props?.scriptSource,
      secureApi: props?.secureApi
    };
    hCaptchaLoader(mountParams).then(handleOnLoad, handleError).catch(handleError);
    apiScriptRequest = true;
  };
  const isApiReady = () => typeof window.hcaptcha !== "undefined";
  const isReady = () => {
    const { isRemoved } = state;
    return isApiReady() && !isRemoved;
  };
  let captcha_ref;
  const [state, setState] = createStore({
    isRemoved: false,
    captchaId: null
  });
  const renderCaptcha = (onReady) => {
    if (!captcha_ref) return;
    if (!isApiReady()) return;
    const renderParams = Object.assign({
      "open-callback": handleOpen,
      "close-callback": handleClose,
      "error-callback": handleError,
      "chalexpired-callback": handleChallengeExpired,
      "expired-callback": handleExpire,
      "callback": handleSubmit
    }, props, {
      hl: props.hl || props.languageOverride,
      languageOverride: void 0
    });
    const captchaId = hcaptcha.render(captcha_ref, renderParams);
    setState({ isRemoved: false, captchaId });
    onReady?.();
  };
  const resetCaptcha = () => {
    if (!isReady() || !state.captchaId) return;
    hcaptcha.reset(state.captchaId);
  };
  const removeCaptcha = (callback) => {
    if (!isReady() || !state.captchaId) return;
    setState({ isRemoved: true });
    hcaptcha.remove(state.captchaId);
    callback?.();
  };
  const handleOnLoad = () => {
    renderCaptcha(() => {
      props.onLoad?.(hcaptcha_functions);
    });
  };
  const executeSync = (rqdata) => {
    if (!isReady() || !state.captchaId) return;
    return hcaptcha.execute(state.captchaId, { async: false, rqdata });
  };
  const execute = async (rqdata) => {
    if (!isReady() || !state.captchaId) return;
    const response = await hcaptcha.execute(state.captchaId, { async: true, rqdata });
    return response;
  };
  const setData = (data) => {
    if (!isReady() || !state.captchaId) return;
    return hcaptcha.setData(state.captchaId, data);
  };
  const getResponse = () => {
    if (!state.captchaId) return null;
    return hcaptcha.getResponse(state.captchaId);
  };
  const getRespKey = () => {
    if (!state.captchaId) return null;
    return hcaptcha.getRespKey(state.captchaId);
  };
  const hcaptcha_functions = {
    execute,
    executeSync,
    getRespKey,
    getResponse,
    removeCaptcha,
    renderCaptcha,
    resetCaptcha,
    setData
  };
  const handleSubmit = () => {
    if (!isApiReady() || state.isRemoved || !state.captchaId) return;
    const token = hcaptcha.getResponse(state.captchaId);
    const ekey = hcaptcha.getRespKey(state.captchaId);
    props.onVerify?.(token, ekey);
  };
  const handleExpire = () => {
    if (!isReady() || !state.captchaId) return;
    hcaptcha.reset(state.captchaId);
    props.onExpire?.();
  };
  const handleError = (event) => {
    if (isReady() && state.captchaId) {
      hcaptcha.reset(state.captchaId);
    }
    props.onError?.(event);
  };
  const handleOpen = () => {
    if (!isReady()) return;
    props.onOpen?.();
  };
  const handleClose = () => {
    if (!isReady()) return;
    props.onClose?.();
  };
  const handleChallengeExpired = () => {
    if (!isReady()) return;
    props.onChallengeExpired?.();
  };
  onMount(() => {
    if (isApiReady()) {
      renderCaptcha();
    }
    loadCaptcha();
  });
  onCleanup(() => {
    if (!isReady() || !state.captchaId) return;
    hcaptcha.reset(state.captchaId);
    hcaptcha.remove(state.captchaId);
  });
  createEffect(on([
    () => props.sitekey,
    () => props.size,
    () => props.theme,
    () => props.tabindex,
    () => props.languageOverride,
    () => props.endpoint
  ], () => removeCaptcha(() => renderCaptcha())));
  return <div
    ref={captcha_ref}
    id={props.id}
  />;
};
var hcaptcha_default = HCaptcha;

// src/index.tsx
var src_default = hcaptcha_default;
export {
  src_default as default
};
